import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { AlertCircle, CheckCircle2, ShieldCheck, ArrowLeft, Search, Loader2 } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

const API = BACKEND_URL;

const REOB_AARD_OPTIES = [
  { id: "werkt_niet", label: "Toestel werkt niet meer" },
  { id: "beschadigd", label: "Toestel is beschadigd" },
  { id: "lek", label: "Toestel lekt / drukverlies" },
  { id: "verkeerde_keurdatum", label: "Verkeerde keurdatum aangebracht" },
  { id: "monteur_gedrag", label: "Klacht over monteur" },
  { id: "factuur", label: "Klacht over factuur" },
  { id: "anders", label: "Anders" },
];

const KlachtKop = () => (
  <header className="bg-[#110B09] text-white" data-testid="klacht-kop">
    <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <img src="/bpt-logo.webp" alt="Brandpreventie Techniek" className="h-8 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
        <div className="pl-3 border-l border-white/25">
          <div className="text-sm font-semibold">Klachtenportaal</div>
          <div className="text-xs text-white/60">Brandpreventie Techniek B.V.</div>
        </div>
      </div>
      <a href="https://brandpreventietechniek.nl" className="text-xs text-white/70 hover:text-white flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Terug naar website
      </a>
    </div>
  </header>
);

export default function KlachtenPortaal() {
  const [modus, setModus] = useState("indienen"); // indienen | status
  const [statusRef, setStatusRef] = useState("");
  const [statusEmail, setStatusEmail] = useState("");
  const [statusData, setStatusData] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);

  const [form, setForm] = useState({
    naam: "",
    email: "",
    telefoon: "",
    bedrijf: "",
    onderwerp: "",
    beschrijving: "",
    ernst: "gemiddeld",
    debiteurnummer: "",
    werkbonnummer: "",
    reob_type_blustoestel: "",
    reob_serienummer: "",
    reob_aard_klacht: [],
    _website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const toggleAard = (id) => {
    setForm((f) => ({
      ...f,
      reob_aard_klacht: f.reob_aard_klacht.includes(id)
        ? f.reob_aard_klacht.filter((x) => x !== id)
        : [...f.reob_aard_klacht, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/publiek/klachten`, form);
      setResult(r.data);
    } catch (err) {
      if (err?.response?.status === 429) {
        setError("U heeft al meerdere klachten in het afgelopen uur ingediend. Probeer over een uur opnieuw.");
      } else {
        setError(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : err?.response?.status === 422 ? "Controleer de verplichte velden (naam, e-mailadres, onderwerp en omschrijving)." : "Er ging iets mis. Probeer het later opnieuw.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const checkStatus = async (e) => {
    e.preventDefault();
    setStatusError(null);
    setStatusData(null);
    setStatusLoading(true);
    try {
      const r = await axios.get(`${API}/api/publiek/klachten/status/${statusRef.trim()}`, { params: { email: statusEmail.trim() } });
      setStatusData(r.data);
    } catch (err) {
      setStatusError(err?.response?.status === 429 ? "Te veel opvragingen — probeer het over een uur opnieuw." : (typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Geen klacht gevonden met deze referentie en dit e-mailadres."));
    } finally {
      setStatusLoading(false);
    }
  };

  // ==== Success screen ====
  if (result) {
    return (
      <div className="min-h-screen bg-[#F6F5F4] flex flex-col"><KlachtKop /><div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-lg p-8 shadow-xl" data-testid="klacht-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Klacht ontvangen</h1>
          <p className="text-slate-600 mb-6">{result.boodschap}</p>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-6">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Uw referentie</div>
            <div className="font-mono text-lg font-semibold text-slate-900" data-testid="klacht-referentie">
              {result.referentie}
            </div>
            <div className="text-xs text-slate-500 mt-3">
              Bewaar deze referentie. Met de referentie en uw e-mailadres kunt u de status van uw klacht opvragen.
            </div>
          </div>

          <div className="text-sm text-slate-600 space-y-2">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Wij bevestigen de ontvangst van uw klacht uiterlijk binnen 14 dagen.</span>
            </div>
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Wij handelen uw klacht binnen 2 maanden af.</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full mt-6"
            onClick={() => {
              setResult(null);
              setForm({
                naam: "", email: "", telefoon: "", bedrijf: "",
                onderwerp: "", beschrijving: "", ernst: "gemiddeld",
                debiteurnummer: "", werkbonnummer: "",
                reob_type_blustoestel: "", reob_serienummer: "",
                reob_aard_klacht: [], _website: "",
              });
            }}
            data-testid="btn-nieuwe-klacht"
          >
            Nieuwe klacht indienen
          </Button>
        </Card>
      </div></div>
    );
  }

  // ==== Main portal ====
  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      {/* Header */}
      <KlachtKop />

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 pt-8">
        <div className="flex gap-2 mb-6" data-testid="portaal-tabs">
          <button
            onClick={() => setModus("indienen")}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium ${
              modus === "indienen" ? "bg-white border border-b-white border-slate-200 text-slate-900" : "text-slate-500 hover:text-slate-900"
            }`}
            data-testid="tab-indienen"
          >
            Klacht indienen
          </button>
          <button
            onClick={() => setModus("status")}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium ${
              modus === "status" ? "bg-white border border-b-white border-slate-200 text-slate-900" : "text-slate-500 hover:text-slate-900"
            }`}
            data-testid="tab-status"
          >
            Status opvragen
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        {modus === "indienen" && (
          <Card className="p-6 sm:p-8" data-testid="klacht-form">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Dien uw klacht in</h2>
            <p className="text-sm text-slate-600 mb-6">
              Vul onderstaand formulier in. Wij bevestigen de ontvangst binnen 14 dagen en handelen uw klacht binnen 2 maanden af.
            </p>

            <form onSubmit={submit} className="space-y-5">
              {/* Honeypot — verborgen */}
              <input
                type="text"
                name="_website"
                value={form._website}
                onChange={(e) => setForm({ ...form, _website: e.target.value })}
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px" }}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              {/* Contactgegevens */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="naam">Uw naam <span className="text-red-500">*</span></Label>
                  <Input
                    id="naam"
                    required
                    value={form.naam}
                    onChange={(e) => setForm({ ...form, naam: e.target.value })}
                    placeholder="Voor- en achternaam"
                    data-testid="input-naam"
                  />
                </div>
                <div>
                  <Label htmlFor="bedrijf">Bedrijf (optioneel)</Label>
                  <Input
                    id="bedrijf"
                    value={form.bedrijf}
                    onChange={(e) => setForm({ ...form, bedrijf: e.target.value })}
                    placeholder="Uw organisatie"
                    data-testid="input-bedrijf"
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-mailadres <span className="text-red-500">*</span></Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="uw@email.nl"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="telefoon">Telefoonnummer <span className="text-red-500">*</span></Label>
                  <Input
                    id="telefoon"
                    required
                    value={form.telefoon}
                    onChange={(e) => setForm({ ...form, telefoon: e.target.value })}
                    placeholder="+31 6 12345678"
                    data-testid="input-telefoon"
                  />
                </div>
              </div>

              {/* Referentie (optioneel) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-5">
                <div>
                  <Label htmlFor="debiteurnummer">Klantnummer (optioneel)</Label>
                  <Input
                    id="debiteurnummer"
                    value={form.debiteurnummer}
                    onChange={(e) => setForm({ ...form, debiteurnummer: e.target.value })}
                    placeholder="Op uw factuur zichtbaar"
                    data-testid="input-debiteur"
                  />
                </div>
                <div>
                  <Label htmlFor="werkbonnummer">Werkbonnummer (optioneel)</Label>
                  <Input
                    id="werkbonnummer"
                    value={form.werkbonnummer}
                    onChange={(e) => setForm({ ...form, werkbonnummer: e.target.value })}
                    placeholder="Op uw werkbon"
                    data-testid="input-werkbon"
                  />
                </div>
              </div>

              {/* Klacht */}
              <div className="border-t pt-5">
                <Label htmlFor="onderwerp">Onderwerp <span className="text-red-500">*</span></Label>
                <Input
                  id="onderwerp"
                  required
                  minLength={3}
                  maxLength={200}
                  value={form.onderwerp}
                  onChange={(e) => setForm({ ...form, onderwerp: e.target.value })}
                  placeholder="Korte omschrijving van uw klacht"
                  data-testid="input-onderwerp"
                />
              </div>

              <div>
                <Label htmlFor="ernst">Ernst</Label>
                <Select value={form.ernst} onValueChange={(v) => setForm({ ...form, ernst: v })}>
                  <SelectTrigger data-testid="select-ernst">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="laag">Laag — kleine ongemakken</SelectItem>
                    <SelectItem value="gemiddeld">Gemiddeld — hindert normaal gebruik</SelectItem>
                    <SelectItem value="hoog">Hoog — veiligheidsrisico</SelectItem>
                    <SelectItem value="kritiek">Kritiek — direct gevaar</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="beschrijving">Beschrijving <span className="text-red-500">*</span></Label>
                <Textarea
                  id="beschrijving"
                  required
                  minLength={10}
                  maxLength={5000}
                  rows={5}
                  value={form.beschrijving}
                  onChange={(e) => setForm({ ...form, beschrijving: e.target.value })}
                  placeholder="Beschrijf uw klacht zo volledig mogelijk. Wanneer trad het probleem op? Wat had u verwacht?"
                  data-testid="input-beschrijving"
                />
                <div className="text-xs text-slate-500 mt-1">
                  {form.beschrijving.length} / 5000 tekens
                </div>
              </div>

              {/* REOB deelgebied A — kleine blusmiddelen */}
              <details className="border rounded-md p-4 bg-slate-50">
                <summary className="cursor-pointer text-sm font-medium text-slate-900">
                  Klacht over blustoestel? Klik om extra velden in te vullen (REOB)
                </summary>
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="reob_type">Type blustoestel</Label>
                      <Input
                        id="reob_type"
                        value={form.reob_type_blustoestel}
                        onChange={(e) => setForm({ ...form, reob_type_blustoestel: e.target.value })}
                        placeholder="bv. 6kg poeder"
                        data-testid="input-reob-type"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reob_serie">Serienummer</Label>
                      <Input
                        id="reob_serie"
                        value={form.reob_serienummer}
                        onChange={(e) => setForm({ ...form, reob_serienummer: e.target.value })}
                        placeholder="Op sticker op toestel"
                        data-testid="input-reob-serie"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Aard van de klacht</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {REOB_AARD_OPTIES.map((opt) => (
                        <label key={opt.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={form.reob_aard_klacht.includes(opt.id)}
                            onCheckedChange={() => toggleAard(opt.id)}
                            data-testid={`checkbox-aard-${opt.id}`}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </details>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800" data-testid="error-msg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                size="lg"
                data-testid="btn-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bezig met verzenden...</>
                ) : (
                  "Klacht indienen"
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                Door uw klacht in te dienen gaat u akkoord met het gebruik van uw gegevens
                voor de behandeling ervan. Wij delen uw gegevens niet met derden.
              </p>
            </form>
          </Card>
        )}

        {modus === "status" && (
          <Card className="p-6 sm:p-8" data-testid="status-form">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Status opvragen</h2>
            <p className="text-sm text-slate-600 mb-6">
              Vul uw referentie-code in (bijvoorbeeld <code>REF-A1B2C3D4</code>) en het e-mailadres waarmee u de klacht hebt ingediend.
            </p>

            <form onSubmit={checkStatus} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 mb-4">
              <Input
                required
                pattern="^REF-[A-F0-9]{8}$"
                value={statusRef}
                onChange={(e) => setStatusRef(e.target.value.toUpperCase())}
                placeholder="REF-XXXXXXXX"
                className="font-mono"
                aria-label="Referentie"
                data-testid="input-ref"
              />
              <Input
                required
                type="email"
                value={statusEmail}
                onChange={(e) => setStatusEmail(e.target.value)}
                placeholder="uw.email@bedrijf.nl"
                aria-label="E-mailadres"
                data-testid="input-status-email"
              />
              <Button type="submit" disabled={statusLoading} className="bg-[#EF3528] hover:bg-[#D42A1E]" data-testid="btn-check-status">
                {statusLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} <span className="ml-1">Opvragen</span>
              </Button>
            </form>

            {statusError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800" data-testid="status-error-msg">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{statusError}</span>
              </div>
            )}

            {statusData && (
              <div className="space-y-3 mt-4" data-testid="status-result">
                <div className="border rounded-md p-4 bg-slate-50">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Referentie</div>
                  <div className="font-mono font-semibold">{statusData.referentie}</div>
                </div>
                <div className="border rounded-md p-4">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Onderwerp</div>
                  <div className="font-medium">{statusData.onderwerp}</div>
                </div>
                <div className="border rounded-md p-4 bg-blue-50">
                  <div className="text-xs text-blue-600 uppercase tracking-wider mb-1">Status</div>
                  <div className="font-medium text-blue-900">{statusData.status_label}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="border rounded-md p-4">
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Ingediend op</div>
                    <div className="text-sm">{new Date(statusData.aangemaakt_op).toLocaleDateString("nl-NL")}</div>
                  </div>
                  <div className="border rounded-md p-4">
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Deadline</div>
                    <div className="text-sm">{new Date(statusData.afhandeling_deadline).toLocaleDateString("nl-NL")}</div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      <footer className="border-t border-slate-200 bg-slate-50 mt-16">
        <div className="max-w-4xl mx-auto px-4 py-6 text-xs text-slate-500 flex items-center justify-between">
          <span>© {new Date().getFullYear()} Brandpreventie Techniek B.V. · Laan van de Kreeft 100, 7324 BX Apeldoorn</span>
          <Link to="/login" className="hover:text-slate-900">Medewerker inloggen</Link>
        </div>
      </footer>
    </div>
  );
}
